import { z } from 'zod'

import { reservableCategorySchema } from './reservationCalendarSchemas'

export const createReservationParamsSchema = z.object({
  contractUuid: z.string().uuid(),
})

export const createReservationBodySchema = z.object({
  timeSlotIds: z.array(z.number()).min(1),
  reservationCalendarId: z.number(),
  validUntil: z.string().nullable().optional(),
  sendConfirmationEmail: z.boolean().default(true),
})

export const reservationCalendarMetaSchema = z.object({
  city: z.string(),
  equipmentCategory: z
    .enum(['washing_machine', 'allergy_washing_machine', 'dryer', 'mangle'])
    .nullable(),
  equipment: z.string().nullable(),
  realPropertyArea: z.string().nullable(),
  realPropertyBuilding: z.string().nullable(),
  realPropertyBuildingPart: z.string().nullable(),
  streetName: z.string().nullable(),
  streetNumber: z.string().nullable(),
})

export const reservationListItemResponseSchema = z.object({
  calendarMeta: reservationCalendarMetaSchema,
  reservableCategory: reservableCategorySchema,
  endsAt: z.string().datetime(),
  id: z.number(),
  recurring: z.boolean(),
  reservationCalendarId: z.number(),
  startsAt: z.string().datetime(),
  hasLockAccess: z.boolean(),
  reservationRecurrenceId: z.number().nullable().optional(),
  hasPaytrailPayment: z.boolean().optional(),
  complimentarySlotsUsed: z.number().optional(),
})

export const listReservationsResponseSchema = z.array(
  reservationListItemResponseSchema
)

export const pincodeResponseSchema = z.object({
  pincode: z.string().nullable(),
})

export const getReservationPincodeParamsSchema = z.object({
  contractUuid: z.string().uuid(),
  reservationId: z.string(),
})

export const getReservationRecurrencePincodeParamsSchema = z.object({
  contractUuid: z.string().uuid(),
  reservationRecurrenceId: z.string(),
})

export const getContractPartPincodeParamsSchema = z.object({
  contractUuid: z.string().uuid(),
  contractPartId: z.string(),
})

export const cancelReservationBodySchema = z.object({
  reservationId: z.number(),
})

export const cancelRecurringReservationBodySchema = z.object({
  reservationRecurrenceId: z.number(),
})

export const terminateReservationBodySchema = z.object({
  reservationRecurrenceId: z.number(),
  terminateFromDate: z.string().date(),
})

export const createSingleReservationSchema = z.object({
  timeSlotIds: z.array(z.number()).min(1),
  reservationCalendarId: z.number(),
  additionalDetails: z.string().optional().nullable(),
})

export const paymentInformationSchema = z.object({
  language: z.enum(['FI', 'SV', 'EN']),
  returnUrl: z.string().url().optional(),
})

export const createSingleReservationBodySchema = z.object({
  reservation: createSingleReservationSchema,
  paymentInformation: paymentInformationSchema,
  sendConfirmationEmail: z.boolean().default(true),
})

export const createSingleReservationResponseSchema = z.object({
  href: z.string().url().optional(),
  accepted: z.boolean(),
})

export type CreateReservationParams = z.infer<
  typeof createReservationParamsSchema
>
export type CreateReservationBody = z.infer<typeof createReservationBodySchema>
export type CancelReservationBody = z.infer<typeof cancelReservationBodySchema>
export type CancelRecurringReservationBody = z.infer<
  typeof cancelRecurringReservationBodySchema
>
export type TerminateReservationBody = z.infer<
  typeof terminateReservationBodySchema
>
export type ReservationResponse = z.infer<
  typeof reservationListItemResponseSchema
>
export type ReservationCalendarMeta = z.infer<
  typeof reservationCalendarMetaSchema
>
export type EquipmentCategory = NonNullable<
  ReservationCalendarMeta['equipmentCategory']
>
export type CreateSingleReservationBody = z.infer<
  typeof createSingleReservationBodySchema
>
export type CreateSingleReservationResponse = z.infer<
  typeof createSingleReservationResponseSchema
>
